import { Component, OnDestroy, OnInit } from '@angular/core';
import { Product, RoutingService } from '@spartacus/core';
import { ToasterService } from '../../../../components/toaster/toaster.service';
import { BehaviorSubject, Subscription, take } from 'rxjs';
import { OrderEntriesSource, OrderEntry } from '@spartacus/cart/base/root';
import { ProductAuxService } from '../../product/_core/product-aux.service';
import { initialStatus, successStatus } from '../../../../core/models/request-status.model';
import { ThirdPartyProductPrices } from '../../../../core/models/product.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NDBSQuickOrderService } from './ndbs-quick-order.service';
import { SimpleEntry } from '../../../../core/models/orderEntry.model';
import { ModalService } from '../../../../components/modal-list/modal-list.service';
import { ModalQuickOrderResetConfirmationComponent } from './components/modal-reset-confirmation/modal-reset-confirmation.component';
import { LoaderService } from '../../../../components/loader/loader.service';
import { CustomActiveCartService } from '../_core/active-cart.service';
import { ExportConfig, ImportExportConfig } from '@spartacus/cart/import-export/core';

@Component({
  selector: 'ndbs-quick-order',
  templateUrl: './ndbs-quick-order.component.html',
  styleUrl: './ndbs-quick-order.component.scss',
  standalone: false,
})
export class NDBSQuickOrderComponent implements OnInit, OnDestroy {
  OrderEntriesSource = OrderEntriesSource;
  entries$ = new BehaviorSubject<OrderEntry[]>([]);
  form: FormGroup = this.fb.group({});
  subscription = new Subscription();

  exportConfigSampleTemplate?: ExportConfig = {
    ...this.importExportConfig.cartImportExport?.export,
    fileOptions: {
      ...this.importExportConfig.cartImportExport?.export?.fileOptions,
      fileName: 'template',
      extension: this.importExportConfig.cartImportExport?.export?.fileOptions?.extension || 'csv',
      type: this.importExportConfig.cartImportExport?.export?.fileOptions?.type || 'defaultType',
    },
  };

  uploadForm = this.fb.group({
    upload: ['', []],
  });

  constructor(
    private fb: FormBuilder,
    private toasterService: ToasterService,
    private productAuxService: ProductAuxService,
    private ndbsQuickOrderService: NDBSQuickOrderService,
    private routingService: RoutingService,
    private modalService: ModalService,
    private loaderService: LoaderService,
    private activeCartService: CustomActiveCartService,
    private importExportConfig: ImportExportConfig,
  ) {}

  ngOnInit(): void {
    this.listnerForm();
    this.subscription.add(this.activeCartService.requireLoadedCart().subscribe());
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  uploadItens(entries: SimpleEntry[]) {
    const productIds = entries.map(entry => entry.code);
    this.loaderService.start();

    this.productAuxService
      .getProductsByIDS(productIds)
      .pipe(take(1))
      .subscribe(response => {
        this.loaderService.stop();

        if (response.hasError) {
          this.toasterService.displayError(response.message!);
          return;
        }

        response.products?.forEach(product => {
          const qty = entries.find(entry => entry.code === product.code)?.qty;
          this.setEntry(product, qty);
        });
      });
  }

  setEntry(product: Product, qty: number = 1) {
    const entries = [...this.entries$.value];

    if (entries.length >= 25) {
      this.toasterService.displayError('You can only add up to 25 products at a time.');
      return;
    }

    if (entries.find(entry => entry.product?.code === product.code)) {
      const id = this.ndbsQuickOrderService.getID(product.code!);
      this.form
        .get(id)
        ?.get('qty')
        ?.setValue(this.form.get(id)?.get('qty')?.value + qty);
      return;
    }

    this.addControl(product, qty);

    this.entries$.next([
      ...this.entries$.value,
      {
        product: {
          ...product,
          thirdPartyPriceStatus: initialStatus,
          yourPrice: undefined,
          differencePrice: undefined,
        },
        quantity: qty,
      },
    ]);

    setTimeout(() => {
      this.productAuxService
        .getProductsPrices({ productsIDS: [product.code!] })
        .pipe(take(1))
        .subscribe(response => {
          if (response.hasError) {
            this.toasterService.displayError(response.message!);
            return;
          }

          const productPrice = response.products ? response.products[0] : undefined;
          this.updateProductWithPrice(product, productPrice);
        });
    }, 500);
  }

  updateProductWithPrice(product: Product, productPrice?: ThirdPartyProductPrices) {
    const entries = [...this.entries$.value];
    const entry = entries.find(entry => entry.product?.code === product.code);
    const newEntries = entries.filter(entry => entry.product?.code !== product.code);

    if (entry) {
      if (productPrice && productPrice?.yourPrice?.value !== 0) {
        entry.product = {
          ...entry.product,
          thirdPartyPriceStatus: successStatus,
          yourPrice: productPrice.yourPrice,
          differencePrice: productPrice.difference,
        };

        newEntries.push(entry);
      } else {
        this.toasterService.displayError(
          `Infortunatelly, we could not get the price of product <strong>${product.code}</strong> and it will not be added to the cart.`,
        );

        this.removeControl(product.code!);
      }

      this.entries$.next([]);

      setTimeout(() => {
        this.entries$.next(newEntries);
      }, 10);
    }
  }

  addControl(product: Product, qty: number) {
    const id = this.ndbsQuickOrderService.getID(product.code!);

    this.form.addControl(
      id,
      this.fb.group({
        code: [null],
        qty: [null],
      }),
      { emitEvent: false },
    );

    this.form.get(id)?.get('code')?.setValue(product.code, { emitEvent: false });
    this.form.get(id)?.get('qty')?.setValue(qty, { emitEvent: false });

    this.form.updateValueAndValidity();
  }

  removeControl(productCode: string) {
    const id = this.ndbsQuickOrderService.getID(productCode);
    this.form.removeControl(id);
    this.form.updateValueAndValidity();
  }

  removeEntry(productCode: string) {
    const entries = [...this.entries$.value];
    const newEntries = entries.filter(entry => entry.product?.code !== productCode);
    this.entries$.next(newEntries);
  }

  findControlCodeWithZero(): (SimpleEntry | undefined)[] {
    const formValues = this.getFormValue();
    const itemWithZeroQtys = formValues?.filter(item => item.qty === 0) as (SimpleEntry | undefined)[];
    return itemWithZeroQtys;
  }

  listnerForm() {
    this.form.valueChanges.subscribe(() => {
      const items = this.findControlCodeWithZero();

      items.forEach(item => {
        if (item) {
          this.removeEntry(item.code);
          this.removeControl(item.code);
        }
      });
    });
  }

  getFormValue() {
    return Object.values(this.form.controls).map(value => value.value);
  }

  getFakeEntries(): OrderEntry[] {
    return [
      {
        product: {
          partNumber: '123456',
        },
        quantity: 1,
      },

      {
        product: {
          partNumber: '123456',
        },
        quantity: 2,
      },
    ];
  }

  getEntries(): OrderEntry[] {
    const formEntries = this.getFormValue();
    const entries = this.entries$.value;

    const newEntries = entries.map(entry => {
      const formEntry = formEntries.find((formEntry: SimpleEntry) => formEntry.code === entry.product?.code);

      if (formEntry) {
        entry.quantity = formEntry.qty;
      }

      return entry;
    });

    return newEntries;
  }

  submitOrder() {
    const entries = this.getFormValue();
    this.loaderService.start();

    this.productAuxService
      .addToCartInLot(entries)
      .pipe(take(1))
      .subscribe(response => {
        this.loaderService.stop();

        if (response.hasError) {
          this.toasterService.displayError(response.message!);
          return;
        }

        this.activeCartService.reloadActiveCart();
        this.routingService.go({ cxRoute: 'cart' });
      });
  }

  reset() {
    this.form.reset();
    this.uploadForm.reset();
    this.entries$.next([]);
  }

  openResetConfirmation() {
    this.modalService.create({
      id: 'modal-reset-quick-order-confirmation',
      title: 'Reset Quick Order',
      width: 500,
      component: ModalQuickOrderResetConfirmationComponent,
      data: {
        reset: this.reset.bind(this),
      },
    });
  }
}
